
import { Component, Vue, Prop, Emit } from "vue-property-decorator"
import { Getter } from "vuex-class"
import vSelect from "vue-select"
import { DataType, FieldInstance, NewValueSetting } from "@/types"
import ParameterValue from "@/components/Controls/ParameterValue.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { CaseColumn } from "@/types/caseColumns"
import FieldExpression from "@/components/Fields/FieldExpression.vue"
import AddToDate from "@/components/Fields/AddToDate.vue"

import {
	CalculatedValueAddToDate,
	CalculatedValueNumerical,
	CalculationType,
	Field,
	formatFieldsForDefinitionTitle,
	getExpressionFieldIds
} from "@/types/field"

import {
	NewValueSelectorProps,
	NewValueSelectorPropsForExpression,
	NewValueSelectorPropsForField
} from "@/types/rule/newValueSelector"
import { FieldDataTypes } from "@/types/fieldTypes"
import { AddToDateInputArgs } from "@/types/fields/addToDate"

@Component({
	components: {
		"v-select": vSelect,
		"parameter-value": ParameterValue,
		"field-expression": FieldExpression,
		"add-to-date": AddToDate
	}
})
export default class NewValueSelector extends Vue {
	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Prop({ default: null })
	field!: CaseColumn | null

	@Prop({ type: Number, default: NewValueSetting.Manual })
	newValueSetting!: NewValueSetting

	@Prop({ default: null })
	valueProps!: NewValueSelectorProps | null

	@Prop({ default: null })
	valuePropsForField!: NewValueSelectorPropsForField | null

	@Prop({ default: null })
	valuePropsForExpression!: NewValueSelectorPropsForExpression | null

	@Prop({ default: false })
	disabled!: boolean

	// events
	@Emit("newValueChanged")
	private onParameterValueUpdated (newValue: any): any {
		return newValue
	}

	@Emit("newValueChanged")
	private onFieldUpdated (field: CaseColumn): string {
		return field?.filterKey ? field?.filterKey : field?.key
	}

	@Emit("newValueChanged")
	private onFieldExpressionValueUpdated (newExpression: string): CalculatedValueNumerical {
		const numericalExpression: CalculatedValueNumerical = {
			expression: newExpression,
			calculationType: CalculationType.Numerical,
			fields: getExpressionFieldIds(newExpression)
		}

		return numericalExpression
	}

	@Emit("newValueChanged")
	private onAddToDateChanged (eventArgs: AddToDateInputArgs) : CalculatedValueAddToDate {
		return eventArgs.value
	}

	@Emit("newValueLabelChanged")
	private onParameterValueLabelUpdated (newValue: string | null): string | null {
		return newValue
	}

	@Emit("dynamicValueChanged")
	private onDynamicValue (newValue: boolean): boolean {
		return newValue
	}

	// computed props
	private get showParameterValue (): boolean {
		return this.newValueSetting === NewValueSetting.Manual
	}

	private get showFieldSelector (): boolean {
		return this.newValueSetting === NewValueSetting.Field
	}

	private get selectableFields (): CaseColumn[] {
		return this.valuePropsForField?.selectableFields ?? []
	}

	private get showExpressionComponent (): boolean {
		return this.newValueSetting === NewValueSetting.Expression
	}

	private get showFieldExpression (): boolean {
		const fieldDataType = this.field?.fieldDataType as DataType
		switch (fieldDataType) {
			case DataType.number:
				return true
			default:
				break
		}

		return false
	}

	private get fieldOptions (): FieldInstance[] {
		if (this.valuePropsForExpression) {
			return formatFieldsForDefinitionTitle(this.valuePropsForExpression.fieldOptions)
		}

		return []
	}

	private get dateFields (): Field[] {
		if (this.valuePropsForExpression) {
			return this.valuePropsForExpression?.fieldOptions.filter(f => f.dataType === FieldDataTypes.Date || f.dataType === FieldDataTypes.DateTime)
		}

		return []
	}

	private get numericFields (): Field[] {
		if (this.valuePropsForExpression) {
			return this.valuePropsForExpression?.fieldOptions.filter(f => f.dataType === FieldDataTypes.Number)
		}

		return []
	}

	private get expression (): string | null {
		if (this.showFieldExpression) {
			const numericalExpression = this.valuePropsForExpression?.value as CalculatedValueNumerical
			return numericalExpression?.expression
		}

		return null
	}

	private get addToDate (): CalculatedValueAddToDate | null {
		if (!this.showFieldExpression) {
			if (this.valuePropsForExpression?.value) {
				return this.valuePropsForExpression?.value as CalculatedValueAddToDate
			} else {
				const value: CalculatedValueAddToDate = {
					fields: [],
					calculationType: CalculationType.AddToDate,
					dateField: null,
					add: [],
					static: null,
					showTime: true
				}
				return value
			}
		}

		return null
	}

	private get closeOnSelect (): boolean {
		return this.valuePropsForExpression?.closeOnSelect ?? false
	}
}
